import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/InfoRounded';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';

import { type ProviderData, useAuth } from 'components/Auth';
import { renderEventMatchingIcon } from 'components/Calendaring';
import type { CICase } from 'components/CriticalIncident/interfaces';
import { computeTotalTime } from 'components/CriticalIncident/utils/CaseSummaryUtils';
import { Spinner, TruncatedText } from 'components/Shared';
import { type PaginationDetails, Paginator } from 'components/Shared/MaterialTable';
import { PriorityAlert } from 'components/Shared/PriorityAlert';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { toShortTimezone } from 'utils';
import { COUNTRY_CODE, useProviderCountryCode } from 'utils/regionUtils';
import { useTimezoneDiffText } from '../../TimezoneDiffAlert';

import './MobileCICasesList.scss';

export const MobileCICasesList: React.FC<{
    referrals?: CICase[];
    dataLoading: boolean;
    paginationDetails?: PaginationDetails;
    handlePageChange?: (pageNumber: number) => void;
    emptyStateMessage: string;
}> = ({ referrals, dataLoading, paginationDetails, handlePageChange, emptyStateMessage }) => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const { effectiveProviderData = {} as ProviderData } = useAuth();
    const { localeTimeFormat, formatDate } = useTimezoneUtils();
    const isAustralianProvider = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;

    return (
        <React.Fragment>
            {translationsReady && !dataLoading ? (
                <React.Fragment>
                    {referrals && referrals.length === 0 ? (
                        <div className="referrals_no_entries">
                            <p>{emptyStateMessage}</p>
                        </div>
                    ) : (
                        <React.Fragment>
                            <PriorityAlert simple priority="normal" iconTooltip={t('time_zone_hint')}>
                                {t('cases_are_shown_in_your_timezone', {
                                    timezone: toShortTimezone(effectiveProviderData?.timeZone ?? '')
                                })}
                            </PriorityAlert>
                            <List className="ci_cases_mobile_list" sx={{ paddingTop: 0 }}>
                                {referrals?.map((ciCase) => {
                                    const bookingTime = formatDate(
                                        moment(ciCase.interventionStartDateTime),
                                        `MMM Do, ${localeTimeFormat}`
                                    );
                                    const duration = computeTotalTime(
                                        t,
                                        ciCase.interventionStartDateTime,
                                        ciCase.interventionEndDateTime,
                                        true
                                    );
                                    const location = t(ciCase.modalityCode);

                                    return (
                                        <React.Fragment key={`${ciCase.caseId}-${ciCase.activityId}`}>
                                            <ListItem className="ci_cases_mobile_list_item">
                                                <ListItemAvatar className="ci_cases_mobile_list_item_avatar">
                                                    <Avatar>{renderEventMatchingIcon(ciCase.modalityCode.trim())}</Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <span className="ci_cases_mobile_list_item_primary">
                                                            {ciCase.organizationName}
                                                        </span>
                                                    }
                                                    secondary={
                                                        <React.Fragment>
                                                            <span className="ci_cases_mobile_list_item_client_name">
                                                                {ciCase.externalWorkOrderId}
                                                            </span>
                                                            {isAustralianProvider && (
                                                                <span className="ci_cases_mobile_list_item_meta">
                                                                    {t('case_id')}: {ciCase.externalCaseId}
                                                                </span>
                                                            )}
                                                            <span className="ci_cases_mobile_list_item_location">
                                                                <TruncatedText>{location}</TruncatedText>
                                                            </span>
                                                            <span className="ci_cases_mobile_list_item_meta">
                                                                {undefined}
                                                            </span>
                                                            <span className="ci_cases_mobile_list_item_meta">
                                                                <span>{bookingTime}</span>
                                                                <CaseTimezoneDiff clientTimezone={ciCase.timezone} />
                                                                <span>•</span>
                                                                <span>{duration}</span>
                                                            </span>
                                                        </React.Fragment>
                                                    }
                                                />
                                                <IconButton
                                                    className="ci_cases_mobile_list_item_action"
                                                    component={Link}
                                                    to={`/critical_incident/cases/${ciCase.caseId}/activities/${ciCase.activityId}`}
                                                    color="primary"
                                                    aria-label={t('view')}
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                            {paginationDetails && handlePageChange && (
                                <Paginator paginationDetails={paginationDetails} onPageChange={handlePageChange} isInfiniteScroll />
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <Spinner fillParent />
            )}
        </React.Fragment>
    );
};

const CaseTimezoneDiff: React.FC<{ clientTimezone?: string | null }> = ({ clientTimezone }) => {
    const { timezoneDiffText } = useTimezoneDiffText({ clientTimezone });

    if (!timezoneDiffText) {
        return null;
    }

    return (
        <InfoIcon
            color="primary"
            fontSize="small"
            sx={{ marginLeft: 0.5, verticalAlign: 'middle' }}
            data-tooltip={timezoneDiffText}
        />
    );
};
